import { PresignedUploadResult } from 'utils/upload/presignedUpload';
import { UploadParams } from 'utils/upload/types';
import { createProgressHandler } from 'utils/upload/utils/createProgressHandler';
import { uploadLegacy } from 'utils/upload/utils/uploadLegacy';
import { uploadPresigned } from 'utils/upload/utils/uploadPresigned';
import { uploadMultipart } from 'utils/upload/utils/uploadMultipart';

export interface FileUploadOptions {
  fileName: string;
  onProgress?: (loaded: number, total: number) => void;
}

export class FileUploader {
  private abortControllers = new Map<string, AbortController>();

  upload = (
    id: string,
    blob: Blob,
    params: UploadParams,
    options: FileUploadOptions
  ): Promise<PresignedUploadResult | undefined> =>
    this.withAbort(id, async (signal) => {
      const onUploadProgress = createProgressHandler(options.onProgress);

      switch (params.strategy) {
        case 'multipart':
          await uploadMultipart(blob, params, signal, options.onProgress);

          return undefined;
        case 'presigned':
          return uploadPresigned(blob, params, signal, onUploadProgress);
        case 'legacy':
          await uploadLegacy(blob, options.fileName, params, signal, onUploadProgress);

          return undefined;
        // no default
      }
    });

  cancel = (id: string): void => {
    const controller = this.abortControllers.get(id);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(id);
    }
  };

  cancelAll = (): void => {
    for (const controller of this.abortControllers.values()) {
      controller.abort();
    }
    this.abortControllers.clear();
  };

  private withAbort = async <T>(
    id: string,
    run: (signal: AbortSignal) => Promise<T>
  ): Promise<T> => {
    const controller = new AbortController();
    this.abortControllers.set(id, controller);

    try {
      return await run(controller.signal);
    } finally {
      this.abortControllers.delete(id);
    }
  };
}
